import { useEffect } from 'react'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db, getMessagingInstance } from '@/lib/firebase'
import { useAuthStore } from '@/stores/auth'

// Chrome penalizes sites that re-prompt for notifications; ask once, then only
// silently refresh the token on later visits if permission is already granted.
const PROMPTED_KEY = 'push_permission_prompted'

export function usePushToken() {
  const uid = useAuthStore((s) => s.uid())

  useEffect(() => {
    if (!uid) return
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return
    if (Notification.permission === 'denied') return
    if (Notification.permission === 'default' && localStorage.getItem(PROMPTED_KEY)) return

    let cancelled = false

    const register = async () => {
      try {
        if (Notification.permission !== 'granted') {
          localStorage.setItem(PROMPTED_KEY, '1')
          const perm = await Notification.requestPermission()
          if (perm !== 'granted') return
        }

        const messaging = await getMessagingInstance()
        if (!messaging || cancelled) return

        const { getToken } = await import('firebase/messaging')
        const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
        const token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: reg,
        })
        if (!token || cancelled) return

        await setDoc(
          doc(db, 'users', uid, 'fcmTokens', token),
          { token, platform: 'web', updatedAt: serverTimestamp() },
          { merge: true },
        )
      } catch (err: unknown) {
        console.warn('[usePushToken] registration failed:', err)
      }
    }

    void register()
    return () => { cancelled = true }
  }, [uid])
}
